import React, { useState, useEffect } from "react";
import { FaDownload, FaEye, FaFileAlt, FaChevronDown, FaCheckCircle, FaMicrochip, FaDatabase, FaServer, FaIdCard, FaDesktop, FaQuoteLeft, FaUser } from "react-icons/fa";
import { useInView } from "react-intersection-observer";
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Link } from "react-router-dom";
import SEO from '../components/SEO';
import PageHero from '../components/page/PageHero';
import SectionHeading from '../components/page/SectionHeading';
import heroImage from '../assets/consulting/hero-counsel.webp';
import ctaImage from '../assets/consulting/cta-path.webp';

const Consulting = () => {
    const { t } = useTranslation();
    const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);
    const [highlightedResumes, setHighlightedResumes] = useState([]);
    const [loadingResumes, setLoadingResumes] = useState(true);
    const [selectedResumeIndex, setSelectedResumeIndex] = useState(0);

    // Fetch highlighted resumes on mount
    useEffect(() => {
        const fetchHighlightedResumes = async () => {
            try {
                const res = await axios.get('/api/resumes/highlighted');
                if (res.data.success && res.data.resumes.length > 0) {
                    setHighlightedResumes(res.data.resumes);
                }
            } catch (error) {
                console.error(`[${new Date().toISOString()}] Error fetching highlighted resumes:`, error);
            } finally {
                setLoadingResumes(false);
            }
        };
        fetchHighlightedResumes();
    }, []);

    // Colors for consistent theming
    const primaryTextColor = "text-silver-light"; // Very light gray/silver
    const secondaryTextColor = "text-silver-medium"; // Medium gray/silver

    // Case study section animation - now first after hero
    const [caseStudyRef, caseStudyInView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
        rootMargin: "-30px",
    });

    // PDF section animation
    const [contentRef, contentInView] = useInView({
        triggerOnce: true,
        threshold: 0.2,
        rootMargin: "-50px",
    });

    // Default fallback PDF if no highlighted resumes
    const fallbackPdfUrl = "/Curkovic_Kristian_Resume_2026_Engineering.pdf";
    
    // Get current resume to display
    const currentResume = highlightedResumes.length > 0 
        ? highlightedResumes[selectedResumeIndex] 
        : null;
    const currentPdfUrl = currentResume ? currentResume.filePath : fallbackPdfUrl;
    const currentPdfName = currentResume 
        ? currentResume.originalFilename 
        : 'Curkovic_Kristian_Resume_2026_Engineering.pdf';

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = currentPdfUrl;
        link.download = currentPdfName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleView = () => {
        window.open(currentPdfUrl, '_blank');
    };

    const togglePdfViewer = () => {
        setIsPdfViewerOpen(!isPdfViewerOpen);
    };

    return (
        <div className="bg-black font-sans min-h-screen">
            <SEO
                title="Web Development Consulting & Custom Software Strategy | Croatia"
                description="Web development consulting for businesses planning custom websites, web application development, and secure software delivery. Practical strategy, architecture, and implementation support."
                keywords="web development consulting, custom software strategy, custom web development services, web application development consulting, hire web developer Croatia, software architecture consulting"
                url="/consulting"
            />
            <PageHero
                eyebrow={t('consulting.hero.eyebrow', 'Consulting')}
                title={t('consulting.hero.title')}
                subtitle={t('consulting.hero.subtitle')}
                ctaLabel={t('consulting.hero.cta', 'Book a free 30-minute consultation')}
                ctaTo="/contact"
                secondaryLabel={t('consulting.hero.secondary', 'See a case study')}
                secondaryHref="#case-studies"
                proofLine={t('consulting.hero.proof', 'Rated 5.0 on Google · Free consultation · Practical strategy, not a slide deck')}
                backgroundImage={heroImage}
            />

            {/* Case Studies Section - Now First */}
            <section
                id="case-studies"
                ref={caseStudyRef}
                className="scroll-mt-24 py-10 sm:py-14 lg:py-16 bg-black"
            >
                <div className="container mx-auto px-4 sm:px-6 lg:px-4">
                    <div
                        className={`mb-8 sm:mb-10 transition-all duration-1000 transform ${caseStudyInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                    >
                        <SectionHeading
                            eyebrow={t('consulting.caseStudies.eyebrow', 'Case studies')}
                            title={t('consulting.caseStudies.title')}
                            lead={t('consulting.caseStudies.subtitle')}
                        />
                    </div>

                    {/* Riding Facility Case Study - Tighter Professional Design */}
                    <div
                        className={`max-w-4xl mx-auto transition-all duration-1000 transform ${caseStudyInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                        style={{ transitionDelay: "150ms" }}
                    >
                        <div className="rounded-xl lg:rounded-2xl bg-off-black p-5 sm:p-6 lg:p-8 border border-gray-800">
                            {/* Header Row - Badge and Title */}
                            <div className="flex flex-wrap items-center gap-3 mb-4">
                                <span className="px-2.5 py-1 bg-moss/15 text-moss-light text-xs font-semibold rounded-md border border-moss/25 uppercase tracking-wide">
                                    {t('consulting.caseStudies.ridingFacility.badge')}
                                </span>
                            </div>

                            {/* Title & Description */}
                            <h3 className={`text-xl sm:text-2xl lg:text-3xl font-semibold mb-2 ${primaryTextColor}`}>
                                {t('consulting.caseStudies.ridingFacility.title')}
                            </h3>
                            <p className={`text-sm sm:text-base ${secondaryTextColor} mb-6 leading-relaxed max-w-3xl`}>
                                {t('consulting.caseStudies.ridingFacility.description')}
                            </p>

                            {/* Challenge & Solution - Compact Grid */}
                            <div className="grid md:grid-cols-2 gap-4 lg:gap-5 mb-5">
                                {/* The Challenge */}
                                <div className="bg-gray-800/40 rounded-lg p-4 border border-gray-700/40">
                                    <h4 className={`text-sm sm:text-base font-semibold mb-2 ${primaryTextColor} flex items-center gap-2`}>
                                        <span className="w-1.5 h-1.5 bg-amber-400 rounded-full"></span>
                                        {t('consulting.caseStudies.ridingFacility.challenge.title')}
                                    </h4>
                                    <p className={`${secondaryTextColor} text-xs sm:text-sm leading-relaxed`}>
                                        {t('consulting.caseStudies.ridingFacility.challenge.text')}
                                    </p>
                                </div>

                                {/* Our Solution */}
                                <div className="bg-gray-800/40 rounded-lg p-4 border border-gray-700/40">
                                    <h4 className={`text-sm sm:text-base font-semibold mb-3 ${primaryTextColor} flex items-center gap-2`}>
                                        <span className="w-1.5 h-1.5 bg-moss-light rounded-full"></span>
                                        {t('consulting.caseStudies.ridingFacility.solution.title')}
                                    </h4>
                                    <ul className="space-y-2">
                                        <li className={`flex items-start gap-2 ${secondaryTextColor} text-xs sm:text-sm`}>
                                            <FaIdCard className="text-moss-light mt-0.5 flex-shrink-0 text-xs" />
                                            <span>{t('consulting.caseStudies.ridingFacility.solution.items.hardware')}</span>
                                        </li>
                                        <li className={`flex items-start gap-2 ${secondaryTextColor} text-xs sm:text-sm`}>
                                            <FaMicrochip className="text-moss-light mt-0.5 flex-shrink-0 text-xs" />
                                            <span>{t('consulting.caseStudies.ridingFacility.solution.items.firmware')}</span>
                                        </li>
                                        <li className={`flex items-start gap-2 ${secondaryTextColor} text-xs sm:text-sm`}>
                                            <FaDatabase className="text-moss-light mt-0.5 flex-shrink-0 text-xs" />
                                            <span>{t('consulting.caseStudies.ridingFacility.solution.items.database')}</span>
                                        </li>
                                        <li className={`flex items-start gap-2 ${secondaryTextColor} text-xs sm:text-sm`}>
                                            <FaServer className="text-moss-light mt-0.5 flex-shrink-0 text-xs" />
                                            <span>{t('consulting.caseStudies.ridingFacility.solution.items.server')}</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            {/* Delivery & User Experience */}
                            <div className="bg-gray-800/40 rounded-lg p-4 border border-gray-700/40 mb-5">
                                <h4 className={`text-sm sm:text-base font-semibold mb-2 ${primaryTextColor} flex items-center gap-2`}>
                                    <FaDesktop className="text-moss-light text-sm" />
                                    {t('consulting.caseStudies.ridingFacility.delivery.title')}
                                </h4>
                                <p className={`${secondaryTextColor} text-xs sm:text-sm leading-relaxed`}>
                                    {t('consulting.caseStudies.ridingFacility.delivery.text')}
                                </p>
                            </div>

                            {/* Result - Success Banner */}
                            <div className="bg-gradient-to-r from-moss-deep/25 to-moss-night/40 rounded-lg p-4 border border-moss/20 mb-5">
                                <div className="flex items-start gap-3">
                                    <FaCheckCircle className="text-moss-light mt-0.5 flex-shrink-0" />
                                    <div>
                                        <h4 className={`text-sm sm:text-base font-semibold mb-1 ${primaryTextColor}`}>
                                            {t('consulting.caseStudies.ridingFacility.result.title')}
                                        </h4>
                                        <p className={`${secondaryTextColor} text-xs sm:text-sm leading-relaxed`}>
                                            {t('consulting.caseStudies.ridingFacility.result.text')}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Client Testimonial */}
                            <div className="bg-gray-800/30 rounded-lg p-4 border-l-2 border-moss/50 mb-5">
                                <div className="flex items-start gap-3">
                                    <FaQuoteLeft className="text-moss-light/60 flex-shrink-0 text-sm mt-0.5" />
                                    <div>
                                        <p className={`${primaryTextColor} text-sm sm:text-base italic leading-relaxed`}>
                                            {t('consulting.caseStudies.ridingFacility.testimonial.quote')}
                                        </p>
                                        <p className={`${secondaryTextColor} text-xs mt-2 font-medium`}>
                                            — {t('consulting.caseStudies.ridingFacility.testimonial.author')}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Bottom Row - Tech Stack & Highlights */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-gray-700/40">
                                {/* Tech Stack */}
                                <div className="flex flex-wrap gap-1.5">
                                    <span className="px-2 py-1 bg-gray-800/80 text-silver-medium text-xs rounded border border-gray-700/50">
                                        {t('consulting.caseStudies.ridingFacility.techStack.items.wiegand')}
                                    </span>
                                    <span className="px-2 py-1 bg-gray-800/80 text-silver-medium text-xs rounded border border-gray-700/50">
                                        {t('consulting.caseStudies.ridingFacility.techStack.items.mcu')}
                                    </span>
                                    <span className="px-2 py-1 bg-gray-800/80 text-silver-medium text-xs rounded border border-gray-700/50">
                                        {t('consulting.caseStudies.ridingFacility.techStack.items.database')}
                                    </span>
                                    <span className="px-2 py-1 bg-gray-800/80 text-silver-medium text-xs rounded border border-gray-700/50">
                                        {t('consulting.caseStudies.ridingFacility.techStack.items.embedded')}
                                    </span>
                                </div>

                                {/* Success Badges */}
                                <div className="flex flex-wrap gap-1.5">
                                    {t('consulting.caseStudies.ridingFacility.result.highlight').split(' • ').map((item, index) => (
                                        <span 
                                            key={index}
                                            className="px-2 py-1 bg-moss/10 text-moss-light text-xs rounded border border-moss/20 font-medium"
                                        >
                                            {item}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* PDF/Resume Section */}
            <section
                ref={contentRef}
                className="py-10 sm:py-12 lg:py-14 bg-black border-t border-gray-800"
            >
                <div className="container mx-auto px-4 sm:px-6 lg:px-4">
                    <div className="max-w-3xl mx-auto">
                        {/* Section Title if multiple resumes */}
                        {highlightedResumes.length > 1 && (
                            <div className="text-center mb-6">
                                <h2 className={`text-xl sm:text-2xl font-serif font-medium mb-2 ${primaryTextColor}`}>
                                    {t('consulting.resume.teamTitle', 'Our Team')}
                                </h2>
                                <p className={`text-sm ${secondaryTextColor}`}>
                                    {t('consulting.resume.teamSubtitle', 'Meet our consultants')}
                                </p>
                            </div>
                        )}

                        {/* Resume Selector - if multiple highlighted */}
                        {highlightedResumes.length > 1 && (
                            <div className="flex flex-wrap justify-center gap-3 mb-6">
                                {highlightedResumes.map((resume, index) => (
                                    <button
                                        key={resume._id}
                                        onClick={() => {
                                            setSelectedResumeIndex(index);
                                            setIsPdfViewerOpen(false);
                                        }}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 ${
                                            selectedResumeIndex === index
                                                ? 'bg-moss text-black'
                                                : 'bg-gray-800/50 text-silver-medium hover:bg-gray-700/50 hover:text-silver-light border border-gray-700/50'
                                        }`}
                                    >
                                        <FaUser className="text-xs" />
                                        <span className="text-sm font-medium">{resume.consultantName}</span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* PDF Section - Compact */}
                        <div
                            className={`transition-all duration-1000 transform ${contentInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                        >
                            {/* Loading State */}
                            {loadingResumes ? (
                                <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-6 animate-pulse">
                                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-800/50 rounded-xl"></div>
                                    <div className="flex-grow text-center sm:text-left">
                                        <div className="h-6 bg-gray-800/50 rounded w-48 mb-2 mx-auto sm:mx-0"></div>
                                        <div className="h-4 bg-gray-800/50 rounded w-64 mb-2 mx-auto sm:mx-0"></div>
                                        <div className="h-3 bg-gray-800/50 rounded w-32 mx-auto sm:mx-0"></div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-6">
                                        {/* PDF Icon */}
                                        <div className="flex-shrink-0">
                                            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-red-900/30 to-red-800/20 rounded-xl flex items-center justify-center">
                                                <FaFileAlt className="text-2xl sm:text-3xl text-red-400" />
                                            </div>
                                        </div>

                                        {/* PDF Info */}
                                        <div className="flex-grow text-center sm:text-left">
                                            <h3 className={`text-lg sm:text-xl font-semibold mb-1 ${primaryTextColor}`}>
                                                {currentResume 
                                                    ? (currentResume.title || `${currentResume.consultantName}'s Resume`)
                                                    : t('consulting.resume.title')}
                                            </h3>
                                            <p className={`text-sm ${secondaryTextColor} mb-2`}>
                                                {currentResume 
                                                    ? currentResume.consultantName
                                                    : t('consulting.resume.description')}
                                            </p>
                                            <p className={`text-xs ${secondaryTextColor} opacity-60`}>
                                                {t('consulting.resume.type')}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Action Buttons - Compact */}
                                    <div className="flex flex-col sm:flex-row justify-center sm:justify-start gap-2 sm:gap-3 mt-4 sm:mt-5">
                                        <button
                                            onClick={handleView}
                                            className={`group flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 ${primaryTextColor} rounded-lg transition-all duration-300 hover:scale-[1.02] text-sm`}
                                        >
                                            <FaEye className="text-sm" />
                                            <span className="font-medium">{t('consulting.resume.viewPdf')}</span>
                                        </button>
                                        <button
                                            onClick={handleDownload}
                                            className={`group flex items-center justify-center gap-2 px-4 py-2.5 bg-transparent hover:bg-gray-900/50 ${primaryTextColor} rounded-lg transition-all duration-300 border border-gray-700 hover:border-gray-600 text-sm`}
                                        >
                                            <FaDownload className="text-sm" />
                                            <span className="font-medium">{t('consulting.resume.download')}</span>
                                        </button>
                                        <button
                                            onClick={togglePdfViewer}
                                            className={`group flex items-center justify-center gap-2 px-4 py-2.5 bg-transparent hover:bg-gray-900/50 ${primaryTextColor} rounded-lg transition-all duration-300 border border-gray-700 hover:border-gray-600 text-sm`}
                                        >
                                            <span className="font-medium">{isPdfViewerOpen ? t('consulting.resume.hideViewer') : t('consulting.resume.showViewer')}</span>
                                            <FaChevronDown className={`text-xs transition-transform duration-300 ${isPdfViewerOpen ? 'rotate-180' : ''}`} />
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Embedded PDF Viewer */}
                        <div
                            className={`overflow-hidden transition-all duration-700 ease-in-out ${isPdfViewerOpen ? 'max-h-[1200px] opacity-100 mt-5' : 'max-h-0 opacity-0 mt-0'}`}
                        >
                            <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-800/50">
                                <iframe
                                    src={`${currentPdfUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                                    className="w-full rounded-lg shadow-xl border-0"
                                    style={{ height: "85vh", minHeight: "600px", maxHeight: "1000px" }}
                                    title={currentResume ? `${currentResume.consultantName} Resume` : "Riviera Tech Solutions Resume"}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact CTA. Was <a href="/contact">, which left the SPA and
                reloaded the whole bundle on the way to the contact form. */}
            <section className="relative overflow-hidden">
                <img
                    src={ctaImage}
                    alt=""
                    aria-hidden="true"
                    className="h-96 w-full object-cover"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-black/70" />
                <div className="absolute inset-0 flex items-center">
                    <div className="container mx-auto px-4 text-center">
                        <div className="mx-auto max-w-2xl">
                            <h2 className={`mb-4 text-3xl md:text-4xl font-serif font-medium ${primaryTextColor}`}>
                                {t('consulting.contact.title')}
                            </h2>
                            <p className={`${secondaryTextColor} mb-8 mx-auto max-w-lg`}>
                                {t('consulting.contact.description')}
                            </p>
                            <Link
                                to="/contact"
                                className="inline-block rounded-md bg-moss px-8 py-3 text-lg font-medium text-black transition-colors duration-300 hover:bg-moss-light"
                            >
                                {t('consulting.contact.button')}
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Consulting;